/**
 * Detecta si la app está corriendo en modo "standalone" (instalada
 * en pantalla de inicio como PWA) y reporta el timestamp a la BD.
 *
 * El admin/coach usan ese timestamp para saber qué clientes tienen
 * la app instalada (más probable que reciban notificaciones push)
 * vs los que solo la abren desde el navegador.
 *
 * Se ejecuta UNA vez por sesión, no en cada render. Si pasaron más
 * de 1 hora desde el último reporte, vuelve a reportar — útil para
 * el caso donde el cliente desinstala y reinstala.
 */

import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const LAST_REPORT_KEY = 'iftmx:last_standalone_report';
const REPORT_THROTTLE_MS = 60 * 60 * 1000; // 1h

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari + Android Chrome usan distintos APIs
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.startsWith('android-app://')
  );
}

export function useStandaloneTracker(userId: string | null | undefined) {
  useEffect(() => {
    if (!userId) return;
    if (!isStandalone()) return;

    // Throttle: si reportamos hace <1h, no spam la BD
    const last = parseInt(localStorage.getItem(LAST_REPORT_KEY) || '0', 10);
    if (Date.now() - last < REPORT_THROTTLE_MS) return;

    const now = new Date().toISOString();
    supabase
      .from('client_onboarding_profiles')
      .update({ last_standalone_at: now })
      .eq('user_id', userId)
      .then(({ error }) => {
        if (!error) {
          localStorage.setItem(LAST_REPORT_KEY, String(Date.now()));
        }
      });
  }, [userId]);
}
